import React, { useCallback, useEffect, useState } from "react";
import { useIntl } from "react-intl";

import {
  PurClientConfig,
  PurPage,
  PurRecord,
  PurResource,
} from "react-lib/apis/PUR/purApi";
import { readId } from "react-lib/apis/PUR/purRead";
import { receiptOrderIssue } from "react-lib/apis/PUR/purReceiptCreate";

import { Event as MeERPEvent, MeERPSlice, nextMeERPRequestKey } from "./MeERPInterface";
import PurLineTable from "./PurLineTable";
import PurApprovalActions from "./PurApprovalActions";
import PurRequestOrderPreparation from "./PurRequestOrderPreparation";
import { PurchaseDraft } from "./purDrafts";
import { formatPurTimestamp, PUR_STATUS_LABELS } from "./purPresentation";

interface PurResourceListProps {
  onEvent: (event: MeERPEvent) => void;
  meerp: MeERPSlice;
  // Still required by the unconverted write flows (approval, request-order preparation).
  client: PurClientConfig;
  resource: PurResource;
  initialStatus?: string;
  statuses: string[];
  allowAllStatuses?: boolean;
  onChoose?: (record: PurRecord) => void;
  onPrepareOrder?: (draft: PurchaseDraft) => void;
  onPrepareReceipt?: (record: PurRecord) => void;
  chooseLabel?: string;
  onOpenPurchaseOrder?: (id: number) => void;
  enableApproval?: boolean;
  onWorkflowLockChange?: (locked: boolean) => void;
  initialRecordId?: number | null;
}

const PAGE_SIZE = 20;

const ignoreLockChange = (_locked: boolean) => undefined;
const PurResourceList: React.FC<PurResourceListProps> = ({ onEvent, meerp, client, resource, initialStatus = "", statuses, allowAllStatuses = true, onChoose, onPrepareOrder, onPrepareReceipt, chooseLabel = "MEERP_DRAFT_USE_PO", onOpenPurchaseOrder, enableApproval = false, onWorkflowLockChange = ignoreLockChange, initialRecordId = null }) => {
  const intl = useIntl();
  const [status, setStatus] = useState(initialStatus);
  const [offset, setOffset] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const [listRevision, setListRevision] = useState(0);
  const [lookupIdText, setLookupIdText] = useState(initialRecordId === null ? "" : String(initialRecordId));
  const [workflowLocked, setWorkflowLocked] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(initialRecordId);
  const [listKey, setListKey] = useState(() => nextMeERPRequestKey());
  const [detailKey, setDetailKey] = useState(() => nextMeERPRequestKey());
  // Records handed back by the write flows after a successful mutation.
  const [detailOverride, setDetailOverride] = useState<PurRecord | null>(null);
  const handleWorkflowLock = useCallback((locked: boolean) => {
    setWorkflowLocked(locked); onWorkflowLockChange(locked);
  }, [onWorkflowLockChange]);

  useEffect(() => {
    const requestKey = nextMeERPRequestKey();
    setListKey(requestKey);
    onEvent({ message: "GetPurResourcePage", params: { resource, limit: PAGE_SIZE, offset, status, requestKey } });
    // The parent remounts this list when the division/client context changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resource, status, offset, refreshKey, listRevision]);

  useEffect(() => {
    setDetailOverride(null);
    if (selectedId === null) return;
    const requestKey = nextMeERPRequestKey();
    setDetailKey(requestKey);
    onEvent({ message: "GetPurRecordDetail", params: { resource, id: selectedId, requestKey } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resource, selectedId, refreshKey]);

  const listState = meerp.lists[resource];
  const listCurrent = listState?.requestKey === listKey;
  const result: PurPage | null = listCurrent ? listState.page : null;
  const loading = listCurrent ? listState.loading : true;
  const errorId = listCurrent ? listState.errorId : null;

  const detailState = selectedId === null ? undefined : meerp.details[`${resource}:${selectedId}`];
  const detailCurrent = detailState?.requestKey === detailKey;
  const selectedOverride = detailOverride?.id === selectedId ? detailOverride : null;
  const detail: PurRecord | null = selectedOverride ?? (detailCurrent ? detailState.record : null) ?? null;
  const detailLoading = selectedOverride === null && (detailCurrent ? detailState.loading : true);
  const detailErrorId = detailCurrent ? detailState.errorId : null;

  const label = (value: string | number | null | undefined): string => value === null || value === undefined || value === "" ? intl.formatMessage({ id: "MEERP_UNKNOWN" }) : String(value);
  const statusLabel = (value: string): string =>
    PUR_STATUS_LABELS.has(value) ? intl.formatMessage({ id: `MEERP_STATUS_${value}` }) : label(value);
  const recordStatusLabel = (record: PurRecord): string =>
    resource === "purchaseOrders" && record.status === "COMPLETED" && record.percentBilled === null
      ? intl.formatMessage({ id: "MEERP_COMPLETED_WITHOUT_BILLING_DATA" }) : statusLabel(record.status);
  const percent = (value: string | null): string => value == null ? intl.formatMessage({ id: "MEERP_UNKNOWN" }) : intl.formatNumber(Number(value) / 100, { style: "percent", maximumFractionDigits: 2 });
  const isPurchaseOrder = resource === "purchaseOrders";
  const isReceipt = resource === "receipts";
  const pageNumber = Math.floor(offset / PAGE_SIZE) + 1;
  const pageCount = result ? Math.max(1, Math.ceil(result.count / PAGE_SIZE)) : 1;

  return (
    <div className="card-meerp__workspace">
      <div className="card-meerp__toolbar">
        <label htmlFor="meerp-status-filter">{intl.formatMessage({ id: "MEERP_STATUS_FILTER" })}</label>
        <select id="meerp-status-filter" value={status} disabled={workflowLocked} onChange={(event) => {
          setStatus(event.target.value);
          setOffset(0);
          setSelectedId(null);
        }}>
          {allowAllStatuses && <option value="">{intl.formatMessage({ id: "MEERP_STATUS_ALL" })}</option>}
          {statuses.map((value) => <option value={value} key={value}>{statusLabel(value)}</option>)}
        </select>
        <button type="button" className="card-meerp__button card-meerp__secondary" disabled={workflowLocked} onClick={() => setRefreshKey((key) => key + 1)}>
          {intl.formatMessage({ id: "MEERP_REFRESH" })}
        </button>
        {result && <span className="card-meerp__count">{intl.formatMessage({ id: "MEERP_COUNT" }, { count: result.count })}</span>}
      </div>
      <p className="card-meerp__muted">{intl.formatMessage({ id: "MEERP_TIMEZONE_NOTE" })}</p>

      {isPurchaseOrder && <p className="card-meerp__muted">{intl.formatMessage({ id: "MEERP_BILLING_NOT_PAYMENT" })}</p>}
      {!allowAllStatuses && <p className="card-meerp__muted">{intl.formatMessage({ id: "MEERP_SINGLE_STATUS_NOTE" })}</p>}

      {loading && <p role="status" className="card-meerp__message">{intl.formatMessage({ id: "MEERP_LOADING" })}</p>}
      {!loading && errorId && (
        <div role="alert" className="card-meerp__message card-meerp__message--error">
          <span>{intl.formatMessage({ id: errorId })}</span>
          <button className="card-meerp__button" type="button" disabled={workflowLocked} onClick={() => setRefreshKey((key) => key + 1)}>{intl.formatMessage({ id: "MEERP_RETRY" })}</button>
        </div>
      )}
      {!loading && result?.records.length === 0 && <p className="card-meerp__message">{intl.formatMessage({ id: "MEERP_EMPTY" })}</p>}
      {!loading && result && result.records.length > 0 && (
        <div className="card-meerp__table-wrap">
          <table className="card-meerp__table">
            <caption className="card-meerp__sr-only">{intl.formatMessage({ id: "MEERP_ITEMS" })}</caption>
            <thead><tr>
              <th scope="col">{intl.formatMessage({ id: "MEERP_NUMBER" })}</th>
              <th scope="col">{intl.formatMessage({ id: "MEERP_FIELD_DOCUMENT_DATE" })}</th>
              <th scope="col">{intl.formatMessage({ id: "MEERP_CREATED_AT" })}</th>
              <th scope="col">{intl.formatMessage({ id: isPurchaseOrder ? "MEERP_VENDOR" : "MEERP_PROJECT" })}</th>
              <th scope="col">{intl.formatMessage({ id: "MEERP_STORAGE" })}</th>
              <th scope="col">{intl.formatMessage({ id: "MEERP_STATUS" })}</th>
              {isPurchaseOrder && <><th scope="col">{intl.formatMessage({ id: "MEERP_PERCENT_RECEIVED" })}</th><th scope="col">{intl.formatMessage({ id: "MEERP_PERCENT_BILLED" })}</th></>}
              <th scope="col" className="card-meerp__numeric">{intl.formatMessage({ id: isPurchaseOrder ? "MEERP_TOTAL" : "MEERP_ITEMS" })}</th>
              <th scope="col">{intl.formatMessage({ id: "MEERP_DETAIL" })}</th>
            </tr></thead>
            <tbody>{result.records.map((record) => (
              <tr key={record.id} className={selectedId === record.id ? "card-meerp__selected-row" : ""}>
                <td>{record.number}</td>
                <td>{label(record.date)}</td>
                <td>{formatPurTimestamp(intl, record.createdAt)}</td>
                <td>{label(isPurchaseOrder ? record.vendorName : record.projectNumber)}</td>
                <td>{label(record.storageName || record.storageId)}</td>
                <td><span className="card-meerp__status">{recordStatusLabel(record)}</span></td>
                {isPurchaseOrder && <><td>{percent(record.percentReceived)}</td><td>{percent(record.percentBilled)}</td></>}
                <td className="card-meerp__numeric">{isPurchaseOrder ? label(record.totalAmount) : record.itemCount ?? intl.formatMessage({ id: "MEERP_UNKNOWN" })}</td>
                <td><button type="button" className="card-meerp__link" disabled={workflowLocked} onClick={() => setSelectedId(record.id)}>{intl.formatMessage({ id: "MEERP_OPEN_DETAIL" })}</button></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}

      {result && <div className="card-meerp__pagination">
        <button className="card-meerp__button" type="button" disabled={offset === 0 || loading || workflowLocked} onClick={() => { setOffset(Math.max(0, offset - PAGE_SIZE)); setSelectedId(null); }}>{intl.formatMessage({ id: "MEERP_PREVIOUS" })}</button>
        <span>{intl.formatMessage({ id: "MEERP_PAGE" }, { current: pageNumber, total: pageCount })}</span>
        <button className="card-meerp__button" type="button" disabled={offset + PAGE_SIZE >= result.count || loading || workflowLocked} onClick={() => { setOffset(offset + PAGE_SIZE); setSelectedId(null); }}>{intl.formatMessage({ id: "MEERP_NEXT" })}</button>
      </div>}

      <details className="card-meerp__internal-lookup" data-testid="meerp-internal-lookup">
        <summary>{intl.formatMessage({ id: "MEERP_INTERNAL_LOOKUP_SECTION" })}</summary>
        <p className="card-meerp__muted">{intl.formatMessage({ id: "MEERP_INTERNAL_LOOKUP_NOTE" })}</p>
        <form className="card-meerp__search" onSubmit={(event) => {
          event.preventDefault();
          const id = readId(lookupIdText.trim());
          if (workflowLocked || id === null) return;
          if (selectedId === id) setRefreshKey((key) => key + 1);
          else setSelectedId(id);
        }}>
          <label htmlFor="meerp-record-id">{intl.formatMessage({ id: "MEERP_RECORD_ID_LOOKUP" })}</label>
          <input id="meerp-record-id" inputMode="numeric" value={lookupIdText} disabled={workflowLocked} onChange={(event) => setLookupIdText(event.target.value)} />
          <button className="card-meerp__button" type="submit" disabled={workflowLocked || readId(lookupIdText.trim()) === null}>{intl.formatMessage({ id: "MEERP_RECORD_ID_OPEN" })}</button>
        </form>
      </details>

      {selectedId !== null && <section className="card-meerp__detail" aria-label={intl.formatMessage({ id: "MEERP_DETAIL" })}>
        <div className="card-meerp__detail-header">
          <h3>{intl.formatMessage({ id: "MEERP_DETAIL" })}{detail ? ` · ${detail.number}` : ""}</h3>
          <button type="button" className="card-meerp__button card-meerp__secondary" disabled={workflowLocked} onClick={() => setSelectedId(null)}>{intl.formatMessage({ id: "MEERP_CLOSE_DETAIL" })}</button>
        </div>
        {detailLoading && <p role="status">{intl.formatMessage({ id: "MEERP_LOADING" })}</p>}
        {detailErrorId && !detailLoading && <p role="alert" className="card-meerp__error-text">{intl.formatMessage({ id: detailErrorId })}</p>}
        {detail && !detailLoading && <>
          <dl className="card-meerp__facts">
            <div><dt>{intl.formatMessage({ id: "MEERP_RECORD_ID" })}</dt><dd>{detail.id}</dd></div>
            <div><dt>{intl.formatMessage({ id: "MEERP_STATUS" })}</dt><dd>{recordStatusLabel(detail)}</dd></div>
            <div><dt>{intl.formatMessage({ id: "MEERP_FIELD_DOCUMENT_DATE" })}</dt><dd>{label(detail.date)}</dd></div>
            <div><dt>{intl.formatMessage({ id: "MEERP_CREATED_AT" })}</dt><dd>{formatPurTimestamp(intl, detail.createdAt)}</dd></div>
            <div><dt>{intl.formatMessage({ id: "MEERP_EDITED_AT" })}</dt><dd>{formatPurTimestamp(intl, detail.editedAt)}</dd></div>
            <div><dt>{intl.formatMessage({ id: "MEERP_PROJECT" })}</dt><dd>{label(detail.projectNumber)}</dd></div>
            <div><dt>{intl.formatMessage({ id: "MEERP_VENDOR" })}</dt><dd>{label(detail.vendorName)}</dd></div>
            <div><dt>{intl.formatMessage({ id: "MEERP_STORAGE" })}</dt><dd>{label(detail.storageName || detail.storageId)}</dd></div>
            {isReceipt && <>
              <div><dt>{intl.formatMessage({ id: "MEERP_PO_ID" })}</dt><dd>{label(detail.poId)}</dd></div>
              <div><dt>{intl.formatMessage({ id: "MEERP_REJECTED_STORAGE" })}</dt><dd>{label(detail.rejectedStorageName || detail.rejectedStorageId)}</dd></div>
            </>}
            {isPurchaseOrder && <>
              <div><dt>{intl.formatMessage({ id: "MEERP_PERCENT_RECEIVED" })}</dt><dd>{percent(detail.percentReceived)}</dd></div>
              <div><dt>{intl.formatMessage({ id: "MEERP_PERCENT_BILLED" })}</dt><dd>{percent(detail.percentBilled)}</dd></div>
            </>}
            {detail.totalAmount && <div><dt>{intl.formatMessage({ id: "MEERP_TOTAL" })}</dt><dd>{detail.totalAmount}</dd></div>}
            {detail.purpose && <div><dt>{intl.formatMessage({ id: "MEERP_PURPOSE" })}</dt><dd>{detail.purpose}</dd></div>}
            {detail.notes && <div><dt>{intl.formatMessage({ id: "MEERP_NOTES" })}</dt><dd>{detail.notes}</dd></div>}
          </dl>
          <h4>{intl.formatMessage({ id: "MEERP_ITEMS" })}</h4>
          {detail.itemCount === null ? <p>{intl.formatMessage({ id: "MEERP_ITEMS_UNAVAILABLE" })}</p> : detail.items.length === 0 ? <p>{intl.formatMessage({ id: "MEERP_NO_ITEMS" })}</p> : <PurLineTable resource={resource} lines={detail.items} />}
          {enableApproval && resource !== "receipts" && <PurApprovalActions key={`${resource}:${detail.id}`} client={client} kind={resource} record={detail}
            onLockChange={handleWorkflowLock}
            onUpdated={(updated) => { setDetailOverride(updated); setListRevision((key) => key + 1); }} />}
          {resource === "materialRequests" && onPrepareOrder && detail.id === selectedId && <PurRequestOrderPreparation key={`request-order:${detail.id}`}
            client={client} record={detail} locked={workflowLocked} onLockChange={handleWorkflowLock} onPrepared={onPrepareOrder} />}
          {isPurchaseOrder && onPrepareReceipt && detail.id === selectedId && <button className="card-meerp__button" type="button" disabled={workflowLocked || Boolean(receiptOrderIssue(detail))} onClick={() => onPrepareReceipt(detail)}>{intl.formatMessage({ id: "MEERP_DRAFT_USE_PO" })}</button>}
          {isReceipt && onOpenPurchaseOrder && readId(detail.poId) !== null && <button className="card-meerp__button" type="button" disabled={workflowLocked} onClick={() => onOpenPurchaseOrder(readId(detail.poId)!)}>{intl.formatMessage({ id: "MEERP_RECEIPT_OPEN_SOURCE_PO" })}</button>}
          {onChoose && detail.id === selectedId && <button className="card-meerp__button" type="button" disabled={workflowLocked || !detail.items.length || (isPurchaseOrder && Boolean(receiptOrderIssue(detail)))} onClick={() => onChoose(detail)}>{intl.formatMessage({ id: chooseLabel })}</button>}
        </>}
      </section>}
    </div>
  );
};

export default PurResourceList;
